/**
 * Domain Registry - Maps domain keys to their full URLs
 *
 * This registry is used to:
 * 1. Capture the origin domain when users register/request password reset
 * 2. Map short keys to full domain URLs
 * 3. Redirect users back to their original whitelabeled domain
 *
 * IMPORTANT: When adding a new whitelabeled domain, add it here as well as
 * the other locations documented in DOMAIN_WHITELIST_LOCATIONS.md
 *
 * NOTE: This file should be kept in sync with functions/src/util/domain-registry.ts
 */

export interface DomainConfig {
  key: string;
  url: string;
  name: string;
  coachId?: string;
}

/**
 * Registry of all whitelabeled domains
 * Key is a short identifier, value contains full URL and display name
 */
export const DOMAIN_REGISTRY: Record<string, DomainConfig> = {
  // Default/fallback domain
  'tandem': {
    key: 'tandem',
    url: 'https://platform.tandemfinance.app',
    name: 'Tandem Finance',
  },

  // Development/testing domains
  'localhost': {
    key: 'localhost',
    url: 'http://localhost:4200',
    name: 'Local Development',
  },
  'tst': {
    key: 'tst',
    url: 'https://tst.tandemfinance.app',
    name: 'Tandem Test',
  },
  'tst-white-label': {
    key: 'tst-white-label',
    url: 'https://tst.white-label.tandemfinance.app',
    name: 'Tandem Test White Label',
  },

  // Production whitelabeled domains
  'lascarare': {
    key: 'lascarare',
    url: 'https://portal.lascarare.com',
    name: 'Lascarare',
    coachId: 'kfHRctJZveSPQibwafx7Ept09hD2',
  },
  'showcase': {
    key: 'showcase',
    url: 'https://showcase.tandemfinance.app',
    name: 'Showcase',
    coachId: 'HKHQXWr42gdBrm5CGSR44qehZ5B2',
  },
  'ernst': {
    key: 'ernst',
    url: 'https://ernst-coaching.tandemfinance.app',
    name: 'Ernst Coaching',
    coachId: 'Ei3UegUkdmWNy8xynNh2iEQnT4z1',
  },
  'prd': {
    key: 'prd',
    url: 'https://tandem-finance-prd.web.app',
    name: 'Tandem PRD',
    coachId: 'MYxSKUEMKWMORrRTF8lseCQYHhZ2',
  },
  'eliteinvestor': {
    key: 'eliteinvestor',
    url: 'https://academy.eliteinvestor.network',
    name: 'Elite Investor Academy',
    coachId: 'oSXLIIxpepS50aqUCinrydyqgqH2',
  },
  'warroom': {
    key: 'warroom',
    url: 'https://warroom.tandemfinance.app',
    name: 'War Room',
    coachId: 'rwPUxvTnmPfokJlGDv63j9t1cmv2',
  },
};

/**
 * Get the domain key from the current hostname
 * Returns 'tandem' as default if hostname is not recognized
 */
export function getCurrentDomainKey(): string {
  const hostname = window.location.hostname.toLowerCase().replace(/^www\./, '');

  // Check for exact matches first
  for (const [key, config] of Object.entries(DOMAIN_REGISTRY)) {
    const configHost = new URL(config.url).hostname.replace(/^www\./, '');
    if (hostname === configHost || hostname.includes(configHost)) {
      return key;
    }
  }

  // Check for partial matches (e.g., localhost:4200 should match localhost)
  if (hostname.startsWith('localhost')) {
    return 'localhost';
  }

  // Default to tandem
  return 'tandem';
}

/**
 * Get the full URL for a domain key
 * Returns the default tandem URL if key is not found
 */
export function getDomainUrlFromKey(key: string | null): string {
  if (!key) {
    return DOMAIN_REGISTRY['tandem'].url;
  }
  const config = DOMAIN_REGISTRY[key];
  return config?.url || DOMAIN_REGISTRY['tandem'].url;
}

/**
 * Validate that a domain key exists in the registry
 */
export function isValidDomainKey(key: string): boolean {
  return key in DOMAIN_REGISTRY;
}

/**
 * Get the login URL for a domain key
 */
export function getLoginUrlForDomain(key: string | null): string {
  const baseUrl = getDomainUrlFromKey(key);
  return `${baseUrl}/auth/login`;
}

/**
 * Get the correct invite origin URL for a given coach.
 * Returns the coach's white-label domain if one exists,
 * otherwise falls back to the current window origin.
 */
export function getInviteOriginForCoach(coachId: string): string {
  for (const config of Object.values(DOMAIN_REGISTRY)) {
    if (config.coachId === coachId) {
      return config.url;
    }
  }
  return window.location.origin;
}

